import React, { useRef, useState } from 'react';
import { FileUploadProps } from '../types';

const FileUpload: React.FC<FileUploadProps> = ({
  accept,
  multiple = false,
  maxSize,
  onFileSelect,
  onError,
  className = '',
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedby,
  texts = {},
  ...props
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    const validFiles: File[] = [];
    const errors: string[] = [];

    fileArray.forEach((file) => {
      // Check file size
      if (maxSize && file.size > maxSize) {
        errors.push(
          `${file.name} ${texts.fileTooLarge || 'est trop volumineux (max:'} ${formatFileSize(maxSize)})`
        );
        return;
      }

      // Check file type
      if (accept) {
        const acceptedTypes = accept.split(',').map((type) => type.trim());
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        const fileType = file.type;

        const isAccepted = acceptedTypes.some((type) => {
          if (type.startsWith('.')) {
            return fileExtension === type.toLowerCase();
          }
          return (
            fileType === type || fileType.startsWith(type.replace('*', ''))
          );
        });

        if (!isAccepted) {
          errors.push(
            `${file.name} ${texts.fileTypeNotAccepted || "n'est pas un type de fichier accepté"}`
          );
          return;
        }
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      onError?.(errors.join(', '));
      return;
    }

    if (validFiles.length > 0) {
      onFileSelect?.(multiple ? validFiles : [validFiles[0]]);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fileInputRef.current?.click();
    }
  };

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    // Ne pas désactiver le drag si on sort d'un élément enfant
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
  };

  const uploadId = `file-upload-${Math.random().toString(36).substr(2, 9)}`;
  const descriptionId = `${uploadId}-description`;

  return (
    <div
      className={`${className}`}
      {...props}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => e.preventDefault()}
    >
      <input
        ref={fileInputRef}
        id={uploadId}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={(e) => handleFileSelect(e.target.files)}
        className="sr-only"
        aria-describedby={descriptionId}
        aria-label={
          ariaLabel || texts.selectFileLabel || 'Sélectionner un fichier'
        }
      />

      <div
        role="button"
        tabIndex={0}
        aria-label={
          ariaLabel ||
          texts.uploadZoneLabel ||
          'Zone de téléchargement de fichier'
        }
        aria-describedby={descriptionId}
        className={`relative overflow-hidden rounded-3xl transition-all duration-200 cursor-pointer group py-8 rounded-md bg-white dark:bg-gray-800 border-2 ${
          isDragOver
            ? 'border-blue-500 shadow-lg'
            : isFocused
              ? 'border-blue-500 ring-2 ring-blue-200 dark:ring-blue-800'
              : 'border-slate-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500'
        }`}
        onDragEnter={handleDragOver}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        {/* Main Content */}
        <div className="relative p-8 text-center cursor-pointer">
          {/* Icon Container */}
          <div className="mb-6">
            <div
              className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl transition-colors duration-200 ${
                isDragOver
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 group-hover:bg-blue-50 dark:group-hover:bg-blue-900/20 group-hover:text-blue-500 dark:group-hover:text-blue-400'
              }`}
            >
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
            </div>
          </div>

          {/* Text Content */}
          <div className="space-y-4 cursor-pointer">
            <div>
              <h3
                className={`text-xl font-semibold mb-2 transition-colors duration-200 ${
                  isDragOver
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-800 dark:text-gray-100'
                }`}
              >
                {texts.selectFile || 'Sélectionnez votre fichier'}
              </h3>
              <p
                id={descriptionId}
                className={`text-sm transition-colors duration-200 ${
                  isDragOver
                    ? 'text-blue-500 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                {texts.dragDropOrBrowse ||
                  'Glissez-déposez ou parcourez vos fichiers'}
                .{' '}
                {texts.pressEnterOrSpace ||
                  'Appuyez sur Entrée ou Espace pour ouvrir le sélecteur de fichiers.'}
              </p>
            </div>

            {/* File Info Cards */}
            <div className="flex flex-wrap justify-center gap-3 mt-4 cursor-pointer">
              {accept && (
                <div
                  className={`inline-flex items-center px-3 py-1 rounded-lg border text-xs transition-colors duration-200 ${
                    isDragOver
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700'
                      : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-600'
                  }`}
                >
                  <span className="font-medium">{accept}</span>
                </div>
              )}
              {maxSize && (
                <div
                  className={`inline-flex items-center px-3 py-1 rounded-lg border text-xs transition-colors duration-200 ${
                    isDragOver
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700'
                      : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-600'
                  }`}
                >
                  <span className="font-medium">
                    {texts.maxSize || 'Max'} {formatFileSize(maxSize)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileUpload;
